import { Injectable } from '@angular/core';
import { Maintenance, MaintenanceProductServicePageResult, PagedResult, Quotation } from '@interfaces';
import { Apollo } from 'apollo-angular';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { QuerifyService } from '../../services/querify.service';
import { Mutations } from './mutations';
import { Queries } from './queries';

@Injectable({
  providedIn: 'root'
})
export class MaintenancesApi {
  constructor(
    private apollo: Apollo,
    private queries: Queries,
    private mutations: Mutations,
    private querify: QuerifyService
  ) {}

  searchOne(id): Observable<Maintenance> {
    return this.apollo
      .query({
        query: this.queries.getMaintenance(),
        variables: { id: id }
      })
      .pipe(
        map(({ data }) => {
          return data['maintenance'];
        })
      );
  }

  search(query?, options?): Observable<PagedResult> {
    options = this.querify.transformOptions(query, options);
    return this.apollo
      .query({
        query: this.queries.getPaginatedMaintenances(),
        variables: options
      })
      .pipe(map(({ data }) => data['maintenances']));
  }

  searchWithWarranties(query?, options?): Observable<PagedResult> {
    options = this.querify.transformOptions(query, options);
    return this.apollo
      .query({
        query: this.queries.getPaginatedMaintenancesWithWarranties(),
        variables: options
      })
      .pipe(map(({ data }) => data['maintenances']));
  }

  async save(maintenance): Promise<Maintenance> {
    return this.apollo.mutate({
      mutation: this.mutations.createMaintenance(),
      variables: {
        input: maintenance
      }
    }).toPromise()
      .then(({ data }) => data['createMaintenance']);
  }

  update(id, maintenance): Observable<any> {
    return this.apollo.mutate({
      mutation: this.mutations.updateMaintenance(),
      variables: {
        id: id,
        input: maintenance
      }
    });
  }

  reallocateMaintenance(id, reallocate): Observable<any> {
    return this.apollo.mutate({
      mutation: this.mutations.reallocateMaintenance(),
      variables: {
        id: id,
        input: reallocate
      }
    });
  }

  async cancelMaintenance({ id, cancellationReason }): Promise<void> {
    await this.apollo.mutate({
      mutation: this.mutations.cancelMaintenance(),
      variables: {
        id: id,
        input: {
          cancellationReason
        }
      }
    }).toPromise();
  }

  async archiveMaintenance(id: string): Promise<void>{
    await this.apollo.mutate({
      mutation: this.mutations.archiveMaintenance(),
      variables: {
        id: id,
      }
    }).toPromise();
  }

  async unarchiveMaintenance(id: string): Promise<void>{
    await this.apollo.mutate({
      mutation: this.mutations.unarchiveMaintenance(),
      variables: {
        id: id,
      }
    }).toPromise();
  }


  async reopenMaintenance({ id, reopeningReason, scheduleSuggestions }): Promise<void> {
    await this.apollo.mutate({
      mutation: this.mutations.reopenMaintenance(),
      variables: {
        id: id,
        input: {
          reopeningReason,
          scheduleSuggestions
        }
      }
    }).toPromise();
  }

  createMaintenanceComment(comment): Promise<any> {
    return this.apollo
      .mutate({
        mutation: this.mutations.createMaintenanceComment(),
        variables: {
          input: comment
        }
      })
      .toPromise()
      .then(({ data }) => {
        return data['createMaintenanceComment'];
      });
  }

  removeMaintenanceComment(commentId): Observable<any> {
    return this.apollo.mutate({
      mutation: this.mutations.removeMaintenanceComment(),
      variables: {
        id: commentId
      }
    });
  }

  removeMaintenanceLabel(input): Observable<any> {
    return this.apollo.mutate({
      mutation: this.mutations.removeMaintenanceLabel(),
      variables: input
    });
  }

  addMaintenanceLabel(input): Observable<any> {
    return this.apollo.mutate({
      mutation: this.mutations.addMaintenanceLabel(),
      variables: {
        input: input
      }
    });
  }

  saveMaintenanceLabel(input, isRemove): Promise<void> {
    if (!isRemove) {
      return this.addMaintenanceLabel(input).toPromise();
    }
    return this.removeMaintenanceLabel(input).toPromise();
  }

  searchMaintenanceComment(query): Observable<Comment[]> {
    return this.apollo
      .query({
        query: this.queries.getComments(),
        variables: query
      })
      .pipe(
        map(({ data }) => {
          return data['maintenance']['comments'];
        })
      );
  }

  searchMaintenanceQuotations(query): Observable<Quotation[]> {
    return this.apollo
      .query({
        query: this.queries.getQuotations(),
        variables: query
      })
      .pipe(
        map(({ data }) => {
          return data['maintenance']['quotations'];
        })
      );
  }

  cancelMaintenanceScheduleSuggestions(maintenanceId): Observable<any> {
    return this.apollo.mutate({
      mutation: this.mutations.cancelMaintenanceScheduleSuggestions(),
      variables: {
        id: maintenanceId
      }
    });
  }

  acceptMaintenanceScheduleSuggestions(maintenanceId): Observable<any> {
    return this.apollo.mutate({
      mutation: this.mutations.acceptMaintenanceScheduleSuggestions(),
      variables: {
        id: maintenanceId
      }
    });
  }

  updateMaintenanceScheduleSuggestions(maintenanceId, suggestions): Observable<any> {
    return this.apollo.mutate({
      mutation: this.mutations.updateMaintenanceScheduleSuggestions(),
      variables: {
        id: maintenanceId,
        suggestions: suggestions
      }
    });
  }

  public searchMaintenanceProductsServices(variables: Record<string, any>): Observable<MaintenanceProductServicePageResult> {
    return this.apollo
      .query({
        query: this.queries.getProductsServices(),
        variables,
      })
      .pipe(
        map(({ data }) => {
          return data['maintenance']['productsServices'];
        })
      );
  }

  public getMaintenancePendenciesTotalCount(id: string): Observable<number> {
    return this.apollo
      .query({
        query: this.queries.getPendenciesTotalCount(),
        variables: { id: id }
      })
      .pipe(
        map(({ data }) => {
          return data['maintenance']['pendencies']['totalCount'];
        })
      );
  }
}
